import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './afFilter.api';
import { getSelectedAfFilter } from './afFilter.selectors';
import {
  GET_AF_FILTER_LIST,
  GET_AF_FILTER,
  CREATE_AF_FILTER,
  UPDATE_AF_FILTER,
  DELETE_AF_FILTER,
  GO_TO_AF_FILTER_LIST,
  GO_TO_AF_FILTER_EDIT,
  GO_TO_AF_FILTER_VIEW,
  GO_TO_AF_FILTER_CREATE,
  SELECT_AF_FILTER,
  DeleteAfFilterThunkAction,
  UpdateAfFilterThunkAction,
  CreateAfFilterThunkAction,
  SET_AF_FILTER_LOADING,
  SetAfFilterLoadingAction,
  AfFilterActionTypes,
  AfFilter,
  OPEN_AF_FILTER_CREATE_POPUP,
  CLOSE_AF_FILTER_CREATE_POPUP,
  SelectAfFilterAction,
  OpenAfFilterCreatePopupAction,
  CloseAfFilterCreatePopupAction,
} from './afFilter.types';
import { AF_FILTER, SYSTEM_CONFIGURATION_ROUTE, FILTER_SETTING } from '../../constants';

export function openAfFilterCreatePopup(afFilter?: AfFilter): OpenAfFilterCreatePopupAction {
  return {
    type: OPEN_AF_FILTER_CREATE_POPUP,
    payload: { afFilter },
  };
}

export function closeAfFilterCreatePopup(): CloseAfFilterCreatePopupAction {
  return {
    type: CLOSE_AF_FILTER_CREATE_POPUP,
  };
}

export function goToAfFilterList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_AF_FILTER_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${AF_FILTER}`);
  };
}

export function goToAfFilterCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_AF_FILTER_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${AF_FILTER}/create`);
  };
}

export function goToAfFilterEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_AF_FILTER_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${AF_FILTER}/edit`);
  };
}

export function goToAfFilterView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_AF_FILTER_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${AF_FILTER}/view`);
  };
}

export function setAfFilterLoadingAction(
  isLoading: boolean,
): SetAfFilterLoadingAction {
  return {
    type: SET_AF_FILTER_LOADING,
    payload: { isLoading },
  };
}

export function getAfFilterList(deliveryFunctionId: string): AfFilterActionTypes {
  return {
    type: GET_AF_FILTER_LIST,
    payload: api.getAfFilterList(deliveryFunctionId),
  };
}

export function getAfFilter(
  deliveryFunctionId: string, id: string,
): AfFilterActionTypes {
  return {
    type: GET_AF_FILTER,
    payload: api.getAfFilter(deliveryFunctionId, id),
  };
}

export function createAfFilter(
  deliveryFunctionId: string,
  body: WithAuditDetails<AfFilter>,
  history: History,
): CreateAfFilterThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_AF_FILTER,
        payload: api.createAfFilter(deliveryFunctionId, body),
      });

      dispatch(getAfFilterList(deliveryFunctionId));
      dispatch(goToAfFilterList(history));
      showSuccessMessage(i18n.t('afFilter.create.success', { name: body.data?.fgId }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('afFilter.create.failed', { name: body.data?.afId, error: errorMessage }));
    }
  };
}

export function createAfFilterFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<AfFilter>,
): CreateAfFilterThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setAfFilterLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_AF_FILTER,
        payload: api.createAfFilter(deliveryFunctionId, body),
      });

      dispatch(closeAfFilterCreatePopup());
      showSuccessMessage(i18n.t('afFilter.create.success', { name: body.data?.fgId }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('afFilter.create.failed', { name: body.data?.fgId, error: errorMessage }));

    } finally {
      dispatch(setAfFilterLoadingAction(false));
      dispatch(getAfFilterList(deliveryFunctionId));
    }
  };
}

export function updateAfFilter(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<AfFilter>,
  history: History,
): UpdateAfFilterThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_AF_FILTER,
        payload: api.updateAfFilter(deliveryFunctionId, id, body),
      });

      dispatch(getAfFilterList(deliveryFunctionId));
      dispatch(goToAfFilterList(history));
      showSuccessMessage(i18n.t('afFilter.update.success', { name: body.data?.fgId }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('afFilter.update.failed', { name: body.data?.fgId, error: errorMessage }));
    }
  };
}

export function deleteAfFilter(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteAfFilterThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedAfFilter] = getSelectedAfFilter(getState());

    try {
      await dispatch({
        type: DELETE_AF_FILTER,
        payload: api.deleteAfFilter(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('afFilter.delete.success', { name: selectedAfFilter?.fgId }), 5000);
      if (history != null) {
        dispatch(goToAfFilterList(history));
      }
    } catch (e) {
      showError(i18n.t('afFilter.delete.failed', { name: selectedAfFilter?.fgId }));

    } finally {
      dispatch(getAfFilterList(deliveryFunctionId));
    }
  };
}

export function selectAfFilter(
  afFilter: AfFilter[],
): SelectAfFilterAction {
  return {
    type: SELECT_AF_FILTER,
    payload: afFilter,
  };
}
